import { useCallback, useState } from "react"

import { Link } from "react-router-dom"

import type { SolarProposalDto } from "~/api/dto/solar-proposal.dto"
import useSolarLightReach from "~/api/hooks/useSolarLightReach"
import useSolarProposal from "~/api/hooks/useSolarProposal"
import type { LightReachPrefill } from "~/containers/Proposal/Brochure/FinancialSummarySlide/credit-application-modal/LightReach/lightreach-application"

import {
  solarLightReachApplicationBundle,
  solarLightReachReapplyBundle,
  toLightReachFinancingView,
} from "./solar-lightreach-bundle"
import {
  LIGHTREACH_APPLY_LABEL,
  LIGHTREACH_FIRST_YEAR_PAYMENT_LABEL,
  LIGHTREACH_LOAD_ERROR,
  LIGHTREACH_NO_APPLICATION_PROMPT,
  LIGHTREACH_OFFER_LABEL,
  LIGHTREACH_OFFER_REQUIRED_PROMPT,
  LIGHTREACH_PRICING_OUT_OF_DATE,
  LIGHTREACH_TERMS_LABEL,
  OPEN_LIGHTREACH_LOAN_PRODUCT_LABEL,
} from "./solar-lightreach-copy"
import { solarSetupStepPath } from "./solar-setup-paths"
import { SolarLockedNote } from "./SolarLockedNote"
import {
  TPO_PRODUCT_TYPE_LABEL,
  tpoTermsText,
} from "../BatteryOnlyProposal/tpo-terms-copy"
import {
  btnPrimary,
  monthlyUsd,
} from "../ProposalSetup/financing/financing-chrome"
import { LightReachApplication } from "../ProposalSetup/financing/lightreach/LightReachApplication"
import { LightReachFinancingStatus } from "../ProposalSetup/financing/lightreach/LightReachFinancingStatus"

const applicantFromHeader = (
  header: SolarProposalDto | undefined,
): LightReachPrefill => ({
  firstName: header?.homeowner?.firstName ?? ``,
  lastName: header?.homeowner?.lastName ?? ``,
  phoneNumber: header?.homeowner?.phone ?? ``,
  email: header?.homeowner?.email ?? ``,
  address1: header?.address.street ?? ``,
  city: header?.address.city ?? ``,
  state: header?.address.state ?? ``,
  zip: header?.address.postal ?? ``,
})

/**
 * LightReach's financing status on a Solar Proposal. Before an application:
 * the selected offer it applies for, with the way back to Loan Product when
 * there is none or it is out of date, then Apply, which opens the shared
 * application form (server disclosures, SSN, optional co-applicant). After:
 * the shared LightReach status, stipulations and contract, Refresh status,
 * and Re-apply once LightReach declines, freezes or expires it.
 */
export const SolarLightReachFinancingStatus = ({
  proposalId,
}: {
  proposalId: string
}): JSX.Element | null => {
  const lightReachQuery = useSolarLightReach(proposalId)
  const header = useSolarProposal(proposalId).data
  const [form, setForm] = useState<`apply` | `reapply` | null>(null)
  // The dialog re-binds its focus and Escape wiring whenever these change.
  const closeForm = useCallback(() => setForm(null), [])

  const read = lightReachQuery.data ?? null
  if (read == null) {
    return lightReachQuery.isError ? (
      <p role="alert" className="text-[length:var(--step--1)] text-bp-danger">
        {LIGHTREACH_LOAD_ERROR}
      </p>
    ) : null
  }

  const { selectedOffer, application, actions } = read
  const offerMissing = selectedOffer == null
  const offerNotCurrent =
    selectedOffer != null && selectedOffer.quoteStatus !== `current`

  return (
    <div
      className="flex flex-col gap-4"
      data-testid="solar-lightreach-financing"
    >
      {read.financingLocked && read.financingLockReason ? (
        <SolarLockedNote testId="solar-lightreach-financing-locked">
          {read.financingLockReason}
        </SolarLockedNote>
      ) : null}

      {selectedOffer != null ? (
        <dl
          data-testid="solar-lightreach-quote"
          className="grid grid-cols-[minmax(7rem,auto)_1fr] gap-x-4 gap-y-2 rounded-lg border border-bp-border bg-bp-elevated px-4 py-3 text-[length:var(--step--2)]"
        >
          <dt className="text-bp-text-dim">{LIGHTREACH_OFFER_LABEL}</dt>
          <dd className="text-bp-text">{selectedOffer.label}</dd>
          <dt className="text-bp-text-dim">{LIGHTREACH_TERMS_LABEL}</dt>
          <dd className="text-bp-text">
            {`${TPO_PRODUCT_TYPE_LABEL[selectedOffer.productType]} · ${tpoTermsText(
              {
                termYears: selectedOffer.termYears,
                escalationRate: selectedOffer.escalatorPercent / 100,
              },
            )}`}
          </dd>
          <dt className="text-bp-text-dim">
            {LIGHTREACH_FIRST_YEAR_PAYMENT_LABEL}
          </dt>
          <dd className="text-bp-text tabular-nums">
            {selectedOffer.firstYearMonthlyPayment != null
              ? `${monthlyUsd.format(selectedOffer.firstYearMonthlyPayment)}/mo`
              : `—`}
          </dd>
        </dl>
      ) : null}

      {/* Applying, or re-applying once LightReach let the application go,
          waits on a current offer, chosen on Loan Product. */}
      {!application?.holds && (offerMissing || offerNotCurrent) ? (
        <div
          role="status"
          data-testid="solar-lightreach-offer-required"
          className="flex flex-col items-start gap-2"
        >
          <p className="text-[length:var(--step--2)] text-bp-gold">
            {offerMissing
              ? LIGHTREACH_OFFER_REQUIRED_PROMPT
              : LIGHTREACH_PRICING_OUT_OF_DATE}
          </p>
          <Link
            to={solarSetupStepPath(proposalId, `loan-product`)}
            className="text-[length:var(--step--2)] font-semibold text-bp-text underline underline-offset-2"
          >
            {OPEN_LIGHTREACH_LOAN_PRODUCT_LABEL}
          </Link>
        </div>
      ) : null}

      {application == null ? (
        <div className="flex flex-col items-start gap-2">
          <p className="text-[length:var(--step--1)] text-bp-text-muted">
            {LIGHTREACH_NO_APPLICATION_PROMPT}
          </p>
          <button
            type="button"
            data-testid="solar-lightreach-apply"
            disabled={!actions.submitApplication.allowed}
            onClick={() => setForm(`apply`)}
            className={btnPrimary}
          >
            {LIGHTREACH_APPLY_LABEL}
          </button>
          {/* The offer prompt above already says why. */}
          {!actions.submitApplication.allowed &&
          actions.submitApplication.reason &&
          !offerMissing &&
          !offerNotCurrent ? (
            <p
              data-testid="solar-lightreach-apply-reason"
              className="text-[length:var(--step--2)] text-bp-text-muted"
            >
              {actions.submitApplication.reason}
            </p>
          ) : null}
        </div>
      ) : (
        <LightReachFinancingStatus
          bundle={solarLightReachApplicationBundle}
          proposalId={proposalId}
          financing={toLightReachFinancingView(read)}
          onReapply={() => setForm(`reapply`)}
        />
      )}

      {form != null ? (
        <LightReachApplication
          key={form}
          bundle={
            form === `reapply`
              ? solarLightReachReapplyBundle
              : solarLightReachApplicationBundle
          }
          proposalId={proposalId}
          applicant={applicantFromHeader(header)}
          onClose={closeForm}
          onSubmitted={closeForm}
        />
      ) : null}
    </div>
  )
}
